import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TYPES_CONTACT } from '@artosera/shared';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Types de contact de la galerie — `/v1/g/:slug/types-contact` (ARTO-199).
 * Les six types fixes viennent de `@artosera/shared` ; la galerie en ajoute
 * d'autres (`perso-…`), lus ici et proposés partout où l'on choisit un type.
 * Lecture ouverte à `contacts:read`, écriture à la Direction (`settings:write`).
 */
export interface TypeContactPersoPresente {
  readonly code: string;
  readonly libelle: string;
  /** Contacts qui portent ce type : un type encore porté ne se supprime pas. */
  readonly contacts: number;
}

interface ReponseTypesContact {
  readonly fixes: readonly string[];
  readonly personnalises: readonly TypeContactPersoPresente[];
}

const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}/types-contact`;

export const typesContactApi = {
  lister: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseTypesContact>(base(galerie), { galerie, ...appel }),
  creer: (galerie: string, libelle: string, appel?: AppelApi) =>
    api.post<{ type: TypeContactPersoPresente }>(base(galerie), { libelle }, { galerie, ...appel }),
  renommer: (galerie: string, code: string, libelle: string, appel?: AppelApi) =>
    api.patch<{ type: TypeContactPersoPresente }>(
      `${base(galerie)}/${encodeURIComponent(code)}`,
      { libelle },
      { galerie, ...appel },
    ),
  supprimer: (galerie: string, code: string, appel?: AppelApi) =>
    api.delete<void>(`${base(galerie)}/${encodeURIComponent(code)}`, { galerie, ...appel }),
};

const cleTypes = (galerie: string) => cleMetier(galerie, 'types-contact');

export function useTypesContactPerso(galerie: string) {
  return useQuery({
    queryKey: cleTypes(galerie),
    queryFn: (contexte) => typesContactApi.lister(galerie, appelDeRequete(contexte)),
    select: (reponse) => reponse.personnalises,
  });
}

/**
 * Ce que les écrans consomment : les options d'un sélecteur (six fixes, puis
 * ceux de la galerie) et le libellé d'un code, y compris un type que la
 * liste n'a pas (encore) rendu, qui s'affiche alors tel quel.
 */
export function useOptionsTypeContact(galerie: string) {
  const { t } = useTranslation('contacts');
  const perso = useTypesContactPerso(galerie).data;
  return useMemo(() => {
    const libelles = new Map<string, string>([
      ...TYPES_CONTACT.map((code) => [code, t(`types.${code}`)] as const),
      ...(perso ?? []).map((type) => [type.code, type.libelle] as const),
    ]);
    return {
      options: [...libelles].map(([value, label]) => ({ value, label })),
      libelle: (code: string): string => libelles.get(code) ?? code,
    };
  }, [perso, t]);
}

export function useCreerTypeContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (libelle: string, appel) => typesContactApi.creer(galerie, libelle, appel),
      onSuccess: () => void client.invalidateQueries({ queryKey: cleTypes(galerie) }),
    }),
  );
}

export function useRenommerTypeContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ code, libelle }: { code: string; libelle: string }, appel) =>
        typesContactApi.renommer(galerie, code, libelle, appel),
      onSuccess: () => void client.invalidateQueries({ queryKey: cleTypes(galerie) }),
    }),
  );
}

export function useSupprimerTypeContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (code: string, appel) => typesContactApi.supprimer(galerie, code, appel),
      onSuccess: () => void client.invalidateQueries({ queryKey: cleTypes(galerie) }),
    }),
  );
}
